/**
 * Every keyboard shortcut the console answers to, declared once.
 *
 * WHY THIS EXISTS, because the old arrangement looked fine. Bindings were
 * written at six call sites and their captions at three more, so `j` was bound
 * three separate times and described twice, and nothing anywhere could notice
 * when a key and the words under it stopped agreeing. On the approvals queue a
 * caption that lies is not cosmetic: a key that appears to do nothing is
 * indistinguishable from a key that quietly sent a stranger a message.
 *
 * So a row below is the ONLY place a shortcut is written down. `useShortcuts`
 * binds from these rows and the shortcuts drawer renders from the same ones,
 * which does not merely discourage drift, it leaves nothing to drift: there is
 * one string and both surfaces read it.
 *
 * STATE THE PRECONDITION, DO NOT HIDE THE ROW. A shortcut that needs an open
 * card is still listed, with "Requires an open card" printed under it. Hiding it
 * would answer "why did nothing happen" with an empty space, and the reader who
 * wants to know which keys exist is exactly the reader who has not got the
 * precondition yet.
 *
 * FOLDING THE SIDEBAR IS BOUND IN THE SIDEBAR AND DECLARED HERE. It has a scope
 * of its own rather than joining `app`, because the handler map `useShortcuts`
 * takes is exhaustive over its scope: putting this row in `app` would demand
 * that the shell answer a key that belongs to a component two levels below it.
 * The scope says where the key is wired, the group says where a reader looks
 * for it, and those are allowed to differ.
 */

/**
 * Where a shortcut is live, which is also what has to be true before it does
 * anything.
 *
 * A scope is not a screen. `stack` is the same two keys on three different
 * screens, which is the whole reason it is one entry rather than three, and the
 * approvals pane holds three scopes at once because a draft awaiting a decision
 * and a message that failed to send offer different keys from the same pane.
 */
export interface ShortcutScopeMeta {
  /**
   * The sentence printed under every row in this scope, or null when the keys
   * are live wherever you are. A row may say something narrower for itself.
   */
  requires: string | null
}

export const SHORTCUT_SCOPES = {
  /** The shell, so these two are live on every screen. */
  app: { requires: null },
  /** The sidebar itself, which is the only thing that can fold it. */
  sidebar: { requires: null },
  /** A list being worked from the keyboard: the inbox, the queue, approvals. */
  stack: { requires: null },
  /** The inbox screen. */
  inbox: { requires: null },
  /** One card open in the work queue. */
  card: { requires: 'Requires an open card' },
  /** One draft open in approvals, whatever became of it. */
  draft: { requires: 'Requires an open draft' },
  /** A draft nobody has decided on yet, which is the only kind that can be approved. */
  decision: { requires: 'Requires a draft that has not been sent yet' },
  /** A message that was approved, was handed over, and never arrived. */
  retry: { requires: 'Requires a message that did not send' },
  /**
   * The Queue's One at a time mode (`pages/prospecting/review`), which binds
   * its keys itself through `review/common.tsx` `useKeys` and `review/flow.ts`
   * `shortcut()`. Its j and k are the `stack` rows, so they are not repeated
   * here. `review/review-flow.test.ts` holds the two to each other.
   */
  review: { requires: 'Requires the Queue in One at a time' },
} as const satisfies Record<string, ShortcutScopeMeta>

export type ShortcutScope = keyof typeof SHORTCUT_SCOPES

/**
 * The headings in the drawer, in the order they are read.
 *
 * Groups name the surface a reader is thinking about, scopes name what binds
 * the key. They are close but not the same: the three approvals scopes share
 * one group, because somebody looking for "how do I approve this" is not
 * thinking in scopes.
 */
export const SHORTCUT_GROUPS = ['Everywhere', 'Any list', 'The inbox', 'The work queue', 'Approvals', 'One at a time'] as const

export type ShortcutGroup = (typeof SHORTCUT_GROUPS)[number]

export interface Shortcut {
  /** Stable and namespaced by scope, because it is what a call site binds by. */
  id: string
  scope: ShortcutScope
  group: ShortcutGroup
  /**
   * The key as `useHotkeys` reads it: `event.key`, with a `mod+` prefix for the
   * combinations that want Cmd or Ctrl. This is the binding AND what the drawer
   * draws a cap from, which is the point of the whole file.
   */
  binding: string
  title: string
  description: string
  /** Narrower than the scope's sentence, for the rows that need one. */
  requires?: string
}

export const SHORTCUTS = [
  {
    id: 'app.palette',
    scope: 'app',
    group: 'Everywhere',
    binding: 'mod+k',
    title: 'Command palette',
    description: 'Jump to any screen or setting by typing its name.',
  },
  {
    id: 'app.shortcuts',
    scope: 'app',
    group: 'Everywhere',
    binding: 'mod+o',
    title: 'Open keyboard shortcuts',
    description: 'Show this list, and press it again to put it away.',
  },
  {
    id: 'sidebar.collapse',
    scope: 'sidebar',
    group: 'Everywhere',
    binding: 'mod+b',
    title: 'Fold the navigation',
    description: 'Shrink the left column to its icons, and press it again to bring the words back.',
    // Narrower than its scope, because below the width where the column becomes
    // a slide-over there is nothing to fold and the key does nothing. Saying so
    // is cheaper than letting somebody on half a screen conclude the binding is
    // broken.
    requires: 'Requires enough room on screen to show the navigation beside the page',
  },
  {
    id: 'stack.next',
    scope: 'stack',
    group: 'Any list',
    binding: 'j',
    title: 'Next item',
    description: 'Open the row below the one you are reading.',
  },
  {
    id: 'stack.previous',
    scope: 'stack',
    group: 'Any list',
    binding: 'k',
    title: 'Previous item',
    description: 'Open the row above the one you are reading.',
  },
  {
    id: 'inbox.search',
    scope: 'inbox',
    group: 'The inbox',
    binding: '/',
    title: 'Search threads',
    description: 'Put the cursor in the search field above the thread list.',
  },
  {
    id: 'inbox.open',
    scope: 'inbox',
    group: 'The inbox',
    binding: 'Enter',
    title: 'Open the highlighted thread',
    description: 'Open the thread j and k have moved to in the list.',
    requires: 'Requires the list with a thread highlighted',
  },
  {
    id: 'inbox.close',
    scope: 'inbox',
    group: 'The inbox',
    binding: 'Escape',
    title: 'Back to the list',
    description: 'Close the open thread and show the whole list again, filters and all.',
    requires: 'Requires an open thread',
  },
  {
    id: 'card.copy',
    scope: 'card',
    group: 'The work queue',
    binding: 'c',
    title: 'Copy the message',
    description: 'Put the drafted message on your clipboard, ready to paste.',
    requires: 'Requires an open card that carries a drafted message',
  },
  {
    id: 'card.profile',
    scope: 'card',
    group: 'The work queue',
    binding: 'o',
    title: 'Open the profile',
    description: 'Open this person in a new tab.',
    requires: 'Requires an open card that carries a profile link',
  },
  {
    id: 'card.done',
    scope: 'card',
    group: 'The work queue',
    binding: 'd',
    title: 'Done, it is sent',
    description: 'Close the card once you have sent the message yourself.',
  },
  {
    id: 'card.replied',
    scope: 'card',
    group: 'The work queue',
    binding: 'r',
    title: 'They replied',
    description: 'Open the form that records a reply and closes the card.',
  },
  {
    id: 'card.skip',
    scope: 'card',
    group: 'The work queue',
    binding: 's',
    title: 'Skip this one',
    description: 'Open the form that sets the card aside with a reason.',
  },
  {
    id: 'card.dismiss',
    scope: 'card',
    group: 'The work queue',
    binding: 'Escape',
    title: 'Close the open form',
    description: 'Leave the reply or skip form without saving anything.',
  },
  {
    id: 'draft.edit',
    scope: 'draft',
    group: 'Approvals',
    binding: 'e',
    title: 'Edit the copy',
    description: 'Open the editor on the words of this draft.',
  },
  {
    id: 'draft.dismiss',
    scope: 'draft',
    group: 'Approvals',
    binding: 'Escape',
    title: 'Leave the editor',
    description: 'Close the editor or the reject form without saving anything.',
  },
  {
    id: 'decision.approve',
    scope: 'decision',
    group: 'Approvals',
    binding: 'a',
    title: 'Approve the draft',
    description: 'Hand it over to be sent as it stands.',
  },
  {
    id: 'decision.reject',
    scope: 'decision',
    group: 'Approvals',
    binding: 'r',
    title: 'Reject the draft',
    description: 'Open the reject form and pick a reason.',
  },
  {
    id: 'decision.commit',
    scope: 'decision',
    group: 'Approvals',
    binding: 'mod+Enter',
    title: 'Approve while editing',
    description: 'Commit the words you have just edited without leaving the field.',
  },
  {
    id: 'retry.send',
    scope: 'retry',
    group: 'Approvals',
    binding: 'mod+Enter',
    title: 'Send it again',
    description: 'Try delivery once more on a message that never arrived.',
  },
  {
    id: 'review.down',
    scope: 'review',
    group: 'One at a time',
    binding: 'ArrowDown',
    title: 'Move down with the arrow',
    description: 'The arrow keys do what j and k do there: the item below opens.',
  },
  {
    id: 'review.up',
    scope: 'review',
    group: 'One at a time',
    binding: 'ArrowUp',
    title: 'Move up with the arrow',
    description: 'The item above the open one opens.',
  },
  {
    id: 'review.approve',
    scope: 'review',
    group: 'One at a time',
    binding: 'a',
    title: 'Approve the open item',
    description: 'A reply moves to To send. A campaign message is handed to Son of Basel and sent.',
    requires: 'Requires an open reply or message in One at a time',
  },
  {
    id: 'review.skip',
    scope: 'review',
    group: 'One at a time',
    binding: 's',
    title: 'Skip the open reply',
    description: 'Set the reply draft aside. Undo brings it back.',
    requires: 'Requires an open reply under Replies in One at a time',
  },
  {
    id: 'review.edit',
    scope: 'review',
    group: 'One at a time',
    binding: 'e',
    title: 'Edit the open reply',
    description: 'Open the editor on its words.',
    requires: 'Requires an open reply under Replies in One at a time',
  },
  {
    id: 'review.reject',
    scope: 'review',
    group: 'One at a time',
    binding: 'r',
    title: 'Reject the open message',
    description: 'Hold the campaign message back and say why.',
    requires: 'Requires an open message under Messages in One at a time',
  },
  {
    id: 'review.retry',
    scope: 'review',
    group: 'One at a time',
    binding: 't',
    title: 'Try a failed message again',
    description: 'Hand a campaign message that did not send to Son of Basel once more.',
    requires: 'Requires a message under Messages that did not send',
  },
  {
    id: 'review.select',
    scope: 'review',
    group: 'One at a time',
    binding: 'x',
    title: 'Select the open reply',
    description: 'Tick it, to approve or skip several replies at once.',
    requires: 'Requires an open reply under Replies in One at a time',
  },
  {
    id: 'review.escape',
    scope: 'review',
    group: 'One at a time',
    binding: 'Escape',
    title: 'Close the editor or clear the selection',
    description: 'Leave the reply editor without saving, or untick every selected reply.',
  },
  {
    id: 'review.commit',
    scope: 'review',
    group: 'One at a time',
    binding: 'mod+Enter',
    title: 'Save the edit and approve',
    description: 'Keep the words in the reply editor and move the reply to To send.',
    requires: 'Requires the reply editor open in One at a time',
  },
  {
    id: 'review.copy',
    scope: 'review',
    group: 'One at a time',
    binding: 'c',
    title: 'Copy and open the reply',
    description: 'Put the approved reply on the clipboard and open its post in a new tab.',
    requires: 'Requires an open reply under To send',
  },
  {
    id: 'review.posted',
    scope: 'review',
    group: 'One at a time',
    binding: 'p',
    title: 'Say it was posted',
    description: 'After Copy and open, the reply is marked as posted and the next one opens.',
    requires: 'Requires a reply under To send that was just copied and opened',
  },
  {
    id: 'review.not-yet',
    scope: 'review',
    group: 'One at a time',
    binding: 'n',
    title: 'Say it is not posted yet',
    description: 'The question closes and the reply stays under To send.',
    requires: 'Requires a reply under To send that was just copied and opened',
  },
] as const satisfies readonly Shortcut[]

export type ShortcutId = (typeof SHORTCUTS)[number]['id']

/** The ids a given scope owns, so a call site cannot bind a key it does not hold. */
export type ShortcutIdIn<S extends ShortcutScope> = Extract<(typeof SHORTCUTS)[number], { scope: S }>['id']

export function shortcutsInScope(scope: ShortcutScope): readonly Shortcut[] {
  return SHORTCUTS.filter((shortcut) => shortcut.scope === scope)
}

export function shortcutsInGroup(group: ShortcutGroup): readonly Shortcut[] {
  return SHORTCUTS.filter((shortcut) => shortcut.group === group)
}

/**
 * One row by id. It throws on an unknown id rather than returning undefined,
 * because every caller is rendering a key cap and a caption for it: an
 * undefined row would print an empty cap, which is the exact failure this file
 * exists to make impossible.
 */
export function shortcutById(id: ShortcutId): Shortcut {
  const found = SHORTCUTS.find((shortcut) => shortcut.id === id)
  if (found === undefined) throw new Error(`No shortcut is declared with the id ${id}`)
  return found
}

/** What to print under a row: its own sentence, or its scope's, or nothing. */
export function shortcutRequirement(shortcut: Shortcut): string | null {
  return shortcut.requires ?? SHORTCUT_SCOPES[shortcut.scope].requires
}

/**
 * Handlers keyed by shortcut id, turned into the key-keyed map the low level
 * binder takes.
 *
 * This is the seam that makes the registry authoritative rather than decorative.
 * A call site names the thing it wants to happen and never writes the key, so
 * changing `d` to `x` is one edit above and every binding and caption follows.
 */
export function shortcutBindings<H>(
  scope: ShortcutScope,
  handlers: Readonly<Record<string, H>>,
): Record<string, H> {
  const bindings: Record<string, H> = {}
  for (const shortcut of shortcutsInScope(scope)) {
    const handler = handlers[shortcut.id]
    if (handler !== undefined) bindings[shortcut.binding] = handler
  }
  return bindings
}

/**
 * The caps to draw for a binding, given the label this platform uses for its
 * modifier.
 *
 * The modifier is passed in rather than read here because this module is pure
 * data and a browser global in it would make the registry unreadable from a
 * test. `Enter` and `Escape` get glyphs a key cap can hold: spelled out they are
 * wider than the row they sit in.
 */
const CAP_GLYPHS: Readonly<Record<string, string>> = {
  Enter: '↵',
  Escape: 'esc',
}

export function shortcutCaps(binding: string, mod: string): string[] {
  const prefix = 'mod+'
  const modified = binding.startsWith(prefix)
  const key = modified ? binding.slice(prefix.length) : binding
  const cap = CAP_GLYPHS[key] ?? key
  return modified ? [mod, cap] : [cap]
}
